import { RadioTile } from 'components/molecules/RadioTile'
import styled from 'styled-components'
import { useEffect, useRef, useState } from 'react'
import { FormInput } from 'components/molecules/FormInput'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from 'components/atoms/Button'
import { APYCalculationMethod } from 'types/Portfolio/APYCalculation'
import { AUTOMATIC_APY_CALCULATION } from 'constants/apyCalculation'
import { FormTextArea } from 'components/molecules/FormTextArea'

const schema = z.object({
  name: z.string().min(3),
  longDescription: z.string().optional(),
  value: z.number({ invalid_type_error: 'Required number' }),
})

export type ApyCalculationMethodSchema = z.infer<typeof schema>
const getId = (id: keyof ApyCalculationMethodSchema) => id

export interface APYCalculationMethodProps {
  manualValues: ApyCalculationMethodSchema | undefined
  currentMethod: APYCalculationMethod
  liveValue: number
  onSubmit: (method: APYCalculationMethod, args: ApyCalculationMethodSchema | null) => void
}

export const APYCalculationMethodForm = ({
  manualValues,
  currentMethod,
  onSubmit,
  liveValue,
}: APYCalculationMethodProps) => {
  const [calculationType, setCalculationType] = useState<APYCalculationMethod>(currentMethod)

  const formContext = useForm<ApyCalculationMethodSchema>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: currentMethod === 'manual' ? manualValues : { ...AUTOMATIC_APY_CALCULATION, value: liveValue },
  })
  const { handleSubmit, formState } = formContext

  const _onSubmit = handleSubmit((data) => {
    onSubmit(calculationType, calculationType === 'manual' ? data : null)
  })
  const areInputsDisabled = calculationType === 'automatic' || formState.isSubmitting || formState.isSubmitted

  const filledFields = useRef<ApyCalculationMethodSchema>()

  useEffect(() => {
    if (filledFields.current === undefined) {
      filledFields.current = {
        name: manualValues?.name ?? AUTOMATIC_APY_CALCULATION.name,
        longDescription: manualValues?.longDescription ?? AUTOMATIC_APY_CALCULATION.longDescription,
        value: manualValues?.value ?? liveValue,
      }
      return
    }

    if (calculationType === 'automatic') {
      filledFields.current = formContext.getValues()
      formContext.setValue('name', AUTOMATIC_APY_CALCULATION.name, { shouldValidate: true })
      formContext.setValue('longDescription', AUTOMATIC_APY_CALCULATION.longDescription, { shouldValidate: true })
      formContext.setValue('value', liveValue, { shouldValidate: true })
      return
    }

    formContext.setValue('name', filledFields.current?.name ?? manualValues?.name, { shouldValidate: true })
    formContext.setValue('longDescription', filledFields.current?.longDescription ?? manualValues?.longDescription, {
      shouldValidate: true,
    })
    formContext.setValue('value', filledFields.current?.value ?? manualValues?.value, { shouldValidate: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculationType])

  return (
    <Form onSubmit={_onSubmit}>
      <OptionsWrapper>
        <StyledRadioTile
          disabled={formState.isSubmitted}
          selected={calculationType === 'automatic'}
          title="Automatic Calculation"
          onClick={() => setCalculationType('automatic')}
        />
        <StyledRadioTile
          disabled={formState.isSubmitted}
          selected={calculationType === 'manual'}
          title="Manual"
          onClick={() => setCalculationType('manual')}
        />
      </OptionsWrapper>
      <FormProvider {...formContext}>
        <FormInput label="Name" isRequired id={getId('name')} disabled={areInputsDisabled} />
        <FormTextArea
          autogrow
          label="Long Description"
          id={getId('longDescription')}
          description="Long Description will show in the offering view as a tooltip."
          disabled={areInputsDisabled}
        />
        <FormInput label="Value" isRequired id={getId('value')} disabled={areInputsDisabled} isNumber innerSuffix="%" />
      </FormProvider>
      <Button
        wide
        view="primary"
        type="submit"
        disabled={!formState.isValid || formState.isSubmitted}
        loading={formState.isSubmitting}
      >
        Confirm
      </Button>
    </Form>
  )
}

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const OptionsWrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
  flex-direction: row;
  align-self: center;

  @media ${({ theme }) => theme.queries.mobileAndSmaller} {
    flex-direction: column;
  }
`
export const StyledRadioTile = styled(RadioTile)`
  flex: 1 1 0;

  @media ${({ theme }) => theme.queries.mobileAndSmaller} {
    flex: 1;
  }
`
